import ExcelJS from 'exceljs';

/**
 * MIME type for OOXML spreadsheets (.xlsx), used when wrapping the workbook in a Blob.
 * @type {string}
 */
const XLSX_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/**
 * File extension appended to the export file name.
 * @type {string}
 */
const XLSX_EXTENSION = '.xlsx';

/**
 * @typedef {Object} ActivityLogSheet
 * @property {Array<Object>} sheetData   Rows to export.
 * @property {string} sheetName          Worksheet tab name.
 * @property {string[]} sheetFilter      Object keys to pull from each row, in column order.
 * @property {string[]} sheetHeader      Human-readable column headers, aligned to sheetFilter.
 * @property {number[]} [columnWidths]   Optional per-column widths, aligned to sheetFilter.
 */

/**
 * @typedef {Object} ActivityLogExport
 * @property {string} fileName               Base file name (no extension).
 * @property {ActivityLogSheet[]} datas      One entry per worksheet.
 */

/**
 * Builds the ExcelJS column definitions for a single sheet descriptor.
 * @param {ActivityLogSheet} sheet - The sheet descriptor.
 * @returns {Array<{header: string, key: string, width: (number|undefined)}>} Column definitions.
 */
function buildColumns(sheet) {
    const widths = sheet.columnWidths || [];
    return sheet.sheetFilter.map((key, index) => ({
        header: sheet.sheetHeader[index],
        key,
        width: widths[index]
    }));
}

/**
 * Adds one worksheet to the workbook from a sheet descriptor.
 * @param {ExcelJS.Workbook} workbook - The target workbook.
 * @param {ActivityLogSheet} sheet - The sheet descriptor.
 * @returns {void}
 */
function appendWorksheet(workbook, sheet) {
    const worksheet = workbook.addWorksheet(sheet.sheetName);
    worksheet.columns = buildColumns(sheet);
    if (Array.isArray(sheet.sheetData) && sheet.sheetData.length > 0) {
        worksheet.addRows(sheet.sheetData);
    }
}

/**
 * Validates that the export descriptor has at least one sheet to render.
 * @param {ActivityLogExport} logs - The export descriptor.
 * @returns {void}
 * @throws {TypeError} If logs is missing or logs.datas is empty.
 */
function assertHasSheets(logs) {
    if (!logs || !Array.isArray(logs.datas) || logs.datas.length === 0) {
        throw new TypeError('activityLogExcel: logs.datas must contain at least one sheet');
    }
}

/**
 * Builds an .xlsx workbook (as a Blob) from Activity Log data.
 * @param {ActivityLogExport} logs - The export descriptor.
 * @returns {Promise<Blob>} The generated workbook as an OOXML Blob.
 * @throws {TypeError} If logs is missing or logs.datas is empty.
 */
export async function buildActivityLogWorkbook(logs) {
    assertHasSheets(logs);
    const workbook = new ExcelJS.Workbook();
    logs.datas.forEach(sheet => appendWorksheet(workbook, sheet));
    const buffer = await workbook.xlsx.writeBuffer();
    return new Blob([buffer], { type: XLSX_MIME_TYPE });
}

/**
 * Triggers a browser download of a Blob under the given file name.
 * @param {Blob} blob - The file contents.
 * @param {string} fileName - The download file name (extension included).
 * @returns {void}
 */
function triggerDownload(blob, fileName) {
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(objectUrl);
}

/**
 * Builds the workbook and triggers a browser download.
 * @param {ActivityLogExport} logs - The export descriptor.
 * @returns {Promise<void>}
 * @throws {TypeError} If logs is missing or logs.datas is empty.
 * @throws {Error} If the workbook cannot be built.
 */
export async function downloadActivityLogExcel(logs) {
    const blob = await buildActivityLogWorkbook(logs);
    triggerDownload(blob, `${logs.fileName}${XLSX_EXTENSION}`);
}
